import * as React from "react";

import { ActionIcon, Grid, Group, Modal, Paper, Title, Tooltip } from "@mantine/core";
import { useDisclosure, useHotkeys, useOs } from "@mantine/hooks";
import { IconApps, IconFilePlus, IconRotate2, IconRotateClockwise2 } from "@tabler/icons-react";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useCountEvent } from "../../hooks/useCountEvent";
import {
  useCurrentEditorDocument,
  useEditorState,
  useLibraryState,
} from "../../hooks/useEditorState";
import { SoftwareAddonTemplate } from "../../lib/model/addonTemplate";
import { softwareAddonTemplates } from "../../lib/state/addonTemplates";
import { librarySlice } from "../../lib/state/reducer";
import { AddonTemplateCard } from "../addons/AddonTemplateCard";

import { NewDocumentWizard } from "./NewDocumentWizard";
import { PDFDownloadButton } from "./PDFDownloadButton";
import { ShareButton } from "./ShareButton";
import { InlineEditableTitle } from "./text/InlineEditableText";

export const EditorControls = (): JSX.Element => {
  const reduxDispatch = useAppDispatch();
  const canUndo = useEditorState((state) => state.past.length > 0) || false;
  const canRedo = useEditorState((state) => state.future.length > 0) || false;

  const isEditorEmpty = useLibraryState((state) => state.editingEntry === null);

  const countEvent = useCountEvent();

  const [showingAddonSelectModal, { close: closeAddonSelectModal, open: openAddonSelectModal }] =
    useDisclosure(false);

  const documentTitle = useCurrentEditorDocument((state) => state.title, "");
  const documentId = useCurrentEditorDocument((state) => state.id, "");

  const undo = React.useCallback(() => {
    if (canUndo) {
      reduxDispatch(librarySlice.actions.undo());
      countEvent("undo");
    }
  }, [reduxDispatch, canUndo]);
  const redo = React.useCallback(() => {
    if (canRedo) {
      reduxDispatch(librarySlice.actions.redo());
      countEvent("redo");
    }
  }, [reduxDispatch, canRedo]);

  const setName = React.useCallback(
    (newValue: string) => {
      reduxDispatch(librarySlice.actions.setDocumentName(newValue));
      countEvent("rename-document");
    },
    [reduxDispatch]
  );

  useHotkeys([
    ["mod+Z", undo],
    ["mod+shift+Z", redo],
    ["mod+Y", redo],
  ]);

  const [wizardOpen, { open: openWizard, close: closeWizard }] = useDisclosure(isEditorEmpty);

  const os = useOs();
  const modPrefix = ["ios", "macos"].includes(os) ? "⌘" : "Ctrl";

  const openDelay = 500;
  const iconButtonSize = "lg";
  const iconSize = "24";
  const iconVariant = "subtle";
  const iconRadius = "md";

  return (
    <Paper withBorder py={4} px={8} pr={"md"} mt={4} mb={4}>
      <NewDocumentWizard open={wizardOpen} onClose={closeWizard} isEditorEmpty={isEditorEmpty} />
      <SoftwareAddonSelectionModal opened={showingAddonSelectModal} close={closeAddonSelectModal} />
      <Group position="apart" noWrap>
        <Group spacing={4} noWrap>
          <Tooltip label="New checklist document..." openDelay={openDelay} withArrow>
            <ActionIcon
              // variant="subtle"
              color="cyan"
              size={iconButtonSize}
              variant={iconVariant}
              radius={iconRadius}
              onClick={() => {
                openWizard();
                countEvent("open-new-document-wizard");
                //reduxDispatch(librarySlice.actions.newDocument());
                // Might have to give up on this
                // reduxDispatch(autofocusFieldSlice.actions.setAutofocusField(documentTitleId));
              }}
            >
              <IconFilePlus size={iconSize} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Add from template..." openDelay={openDelay} withArrow>
            <ActionIcon
              color="cyan"
              size={iconButtonSize}
              variant={iconVariant}
              radius={iconRadius}
              onClick={() => {
                openAddonSelectModal();
                countEvent("open-add-from-addon-template");
              }}
            >
              <IconApps size={iconSize} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Download as PDF" openDelay={openDelay} withArrow>
            <PDFDownloadButton
              color="cyan"
              size={iconButtonSize}
              variant={iconVariant}
              radius={iconRadius}
              iconSize={iconSize}
            />
          </Tooltip>
          <ShareButton
            actionIconProps={{ size: iconButtonSize, variant: iconVariant, radius: iconRadius }}
            iconSize={iconSize}
          />
        </Group>
        <Group>
          <InlineEditableTitle
            text={{ content: documentTitle, id: documentId, editing: false }}
            placeholder="Document Name"
            order={3}
            justify="center"
            extraPadding={20}
            onSave={setName}
          />
        </Group>
        <Group spacing={4} noWrap>
          <Tooltip label={`Undo (${modPrefix}+Z)`} openDelay={openDelay} withArrow>
            <ActionIcon
              size={iconButtonSize}
              variant={iconVariant}
              radius={iconRadius}
              onClick={undo}
              disabled={!canUndo}
            >
              <IconRotate2 size={iconSize} />
            </ActionIcon>
          </Tooltip>
          <Tooltip
            label={`Redo (${modPrefix}+Shift+Z / ${modPrefix}+Y)`}
            openDelay={openDelay}
            withArrow
          >
            <ActionIcon
              size={iconButtonSize}
              variant={iconVariant}
              radius={iconRadius}
              onClick={redo}
              disabled={!canRedo}
            >
              <IconRotateClockwise2 size={iconSize} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Paper>
  );
};

const SoftwareAddonSelectionModal = ({
  opened,
  close,
}: {
  opened: boolean;
  close: () => void;
}): JSX.Element => {
  const reduxDispatch = useAppDispatch();
  const importAddon = React.useCallback((t: SoftwareAddonTemplate) => {
    reduxDispatch(librarySlice.actions.insertContentsFromTemplate(t));
    close();
  }, []);
  const eventCounter = useCountEvent();

  return (
    <Modal
      centered
      opened={opened}
      onClose={close}
      size="xl"
      title={<Title order={3}>Import from addon template</Title>}
    >
      <Paper>
        <Grid columns={3}>
          {softwareAddonTemplates.map((addon) => (
            <Grid.Col span={1} key={addon.id}>
              <AddonTemplateCard
                addonTemplate={addon}
                primaryActionLabel="Import"
                primaryAction={() => {
                  importAddon(addon);
                  eventCounter("add-from-template", addon.id);
                }}
              />
            </Grid.Col>
          ))}
        </Grid>
      </Paper>
    </Modal>
  );
};
